/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Dropdown, Menu, message, Progress, Tooltip } from 'antd';
import { throttle } from 'lodash';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { queryCache, useMutation } from 'react-query';
import { Upload as TusUpload } from 'tus-js-client';

import { CHUNK_SIZE } from '~src/config';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { addTagFile, createFile, editFile, idOrNull } from '~src/services/api';
import { apiCweb } from '~src/services/api/api-cweb';
import { fadeInKeyframes } from '~src/styles/animations';
import { DbFile } from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import FileProgressContext, {
  AttachmentProgress,
  FileProgress,
  FileProgressCtx,
} from '~src/contexts/FileProgressContext';
import { addOrReplaceItem } from '~src/utils/list';
import { appLog } from '~src/utils/log';
import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import { normalizeMsWordDashes } from '~src/hooks/ms-dash-sanitization';

export interface FileProgressItemProps {
  item: FileProgress | AttachmentProgress;
  setFileProgressContext: FileProgressCtx['setFileProgressContext'];
  fileProgressContext: FileProgressCtx['fileProgressContext'];
  onFileDone: (item: FileProgress, options?: { isFileDelete: boolean }) => void;
  onAttachmentDone: (
    item: FileProgress,
    options?: { isFileDelete: boolean }
  ) => void;
  shouldStartUpload: boolean;
}
export const FileProgressItem: React.FC<FileProgressItemProps> = ({
  item,
  setFileProgressContext,
  fileProgressContext,
  onFileDone,
  onAttachmentDone,
  shouldStartUpload,
}) => {
  // TODO: refactor functionality flow, it is messy -> hard to make changes
  // extra: if you save this file while upload list is visible, it produces unexpected uploads
  const folderId = item.folder?.dbFolder.id || null;
  const projectId = useProjectId();
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const tusUploadRef = useRef<TusUpload>();
  const [percent, setPercent] = useState(0);
  const calledCreateRef = useRef(false);
  const lastPayloadRef = useRef<any>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3; // Set the maximum number of retries

  // single source of truth for “safe” filename used for API/TUS matching / MS-dashes: "---"
  const safeFileName = normalizeMsWordDashes(item.file.name);

  const isPayloadSame = (newPayload: any) => {
    return (
      JSON.stringify(lastPayloadRef.current) === JSON.stringify(newPayload)
    );
  };

  // only enable filesQuery if existing folder
  const isFilesQueryEnabled =
    shouldStartUpload && !item.entityCreated && !!item.folder;
  const filesQuery = useFiles(
    { folderId: folderId ? String(folderId) : null, withVersions: true },
    {
      refetchOnWindowFocus: false,
      enabled: isFilesQueryEnabled,
      onError: (error) => {
        updateFileProgress(item.id, { error });
        // message.error(intl.formatMessage({ id: 'file-upload-failed' }));
        appLog('FileProgressItem -> useFiles -> item', item);
        appLog('FileProgressItem -> useFiles -> err', error);
      },
    }
  );

  const handleVFileSuccess = (dbFile: DbFile) => {
    const baseKeys = {
      projectId: String(projectId),
      folderId: idOrNull(item.folder?.dbFolder.id),
    };
    queryCache.setQueryData<DbFile[]>(['files', baseKeys], (prevFiles) =>
      addOrReplaceItem(prevFiles || [], dbFile)
    );
    // update table
    queryCache.setQueryData<DbFile[]>(
      ['files', { ...baseKeys, withVersions: true }],
      (prevFiles) => addOrReplaceItem(prevFiles || [], dbFile)
    );
    setupTusUpload(dbFile);

    if (item.tags?.length && dbFile.id) {
      item.tags.forEach((tagId) => {
        doAddTagFile({
          projectId: String(projectId),
          versionedFileId: dbFile.id,
          id: tagId,
        });
      });
    }

    if (item.tags?.length) {
      const baseKeys = {
        projectId: String(projectId),
        folderId: idOrNull(item.folder?.dbFolder.id),
      };

      const updateTagsInCache = (files: DbFile[] = []) =>
        files.map((file) =>
          file.id === dbFile.id ? { ...file, tags: item.tags } : file
        );

      queryCache.setQueryData<DbFile[]>(['files', baseKeys], updateTagsInCache);
      queryCache.setQueryData<DbFile[]>(
        ['files', { ...baseKeys, withVersions: true }],
        updateTagsInCache
      );
    }
  };

  const [doCreateFile, mutationCreateFile] = useMutation(createFile, {
    onError: (error) => {
      updateFileProgress(item.id, { error });
      // message.error(intl.formatMessage({ id: 'file-upload-failed' }));
      appLog('FileProgressItem -> createFile -> item', item);
      appLog('FileProgressItem -> createFile -> err', error);
    },
    onSuccess: handleVFileSuccess,
  });

  const [doEditFile, mutationEditFile] = useMutation(editFile, {
    onError: (error) => {
      updateFileProgress(item.id, { error });
      // message.error(intl.formatMessage({ id: 'file-upload-failed' }));
      appLog('FileProgressItem -> editFile -> item', item);
      appLog('FileProgressItem -> editFile -> err', error);
    },
    onSuccess: handleVFileSuccess,
  });

  const [doAddTagFile] = useMutation(addTagFile, {
    onError: (error, item) => {
      appLog('FileProgressItem -> addTagFile -> error', error);
    },
  });

  /** whether file reached binary upload */
  const isPending =
    filesQuery.isLoading ||
    mutationCreateFile.isLoading ||
    mutationEditFile.isLoading;

  // mount, ignore "add callCreateFile etc."
  // trigger createFile mutation
  useEffect(() => {
    if (!shouldStartUpload || item.error || calledCreateRef.current) return;

    if (!isFilesQueryEnabled) {
      calledCreateRef.current = true;

      // 1) Vendor side voucher response (share-based API)
      if (
        item.isVendorVoucherResponse &&
        item.shareId &&
        item.shareKey &&
        item.offerId &&
        item.voucherId
      ) {
        setupTusUploadForVendorVoucherResponse(item, {
          shareId: item.shareId,
          shareKey: item.shareKey,
          offerId: item.offerId,
          voucherId: item.voucherId,
        });
        return;
      }

      // 2) Project side voucher attachment
      if (item.voucherId && item.contractId && item.tenderId) {
        setupTusUploadForVoucher(item, {
          projectId: String(projectId),
          tenderId: item.tenderId,
          contractId: item.contractId,
          voucherId: item.voucherId,
        });
        return;
      }

      // 3) Current correction letter attachment (project side)
      if ((item as any)?.isCorrelationLetterAttachment && item.tenderId) {
        setupTusUploadForCurrentCorrectionLetterAttachment(item, item.tenderId);
        return;
      }

      // 3.5) Instant Message Draft attachment (project side)
      if (
        (item as any)?.isInstantMessageAttachment &&
        item.tenderId &&
        (item as any)?.draftId
      ) {
        setupTusUploadForInstantMessageDraft(
          item,
          item.tenderId,
          (item as any).draftId
        );
        return;
      }

      // 3.6) Evaluation Instant Message attachment (project side)
      if (
        (item as any)?.isEvaluationInstantMessageAttachment &&
        item.tenderId &&
        item.contractId
      ) {
        setupTusUploadForEvaluationInstantMessage(
          item,
          item.tenderId,
          item.contractId
        );
        return;
      }

      // 4) Tender attachments (project side)
      if (item.tenderId) {
        setupTusUploadForTender(item, item.tenderId);
        return;
      }

      // 5) Archive upload (normal files)
      callCreateFile();
    }
  }, [
    isFilesQueryEnabled,
    item.voucherId,
    item.contractId,
    item.tenderId,
    item.isVendorVoucherResponse,
    item.shareId,
    item.shareKey,
    item.offerId,
    shouldStartUpload,
    item.error,
  ]);

  // if filesQuery enabled, when filesQuery.data ready -> check for existing file
  useEffect(() => {
    if (item.error) return;

    if (
      isFilesQueryEnabled &&
      filesQuery.data &&
      !calledCreateRef.current &&
      shouldStartUpload
    ) {
      calledCreateRef.current = true;

      const foundByUploadedVersion = filesQuery.data.find((element) =>
        element.versions?.some((fileVersion) => {
          // Resume mode should only skip files that already have a completed uploaded version.
          return fileVersion.name === safeFileName;
        })
      );

      let found = foundByUploadedVersion;

      if (!found) {
        // Compare against sanitized title as fallback.
        found = filesQuery.data.find((v) => v.title === safeFileName);
      }

      if (
        foundByUploadedVersion &&
        (item as FileProgress).skipIfAlreadyUploaded
      ) {
        // Resume mode: skip already uploaded files instead of uploading them as new versions.
        markFileAsSkipped();
        return;
      }

      if (found) {
        // Edit existing instance of DbFile instead of creating a new one.
        // The server should always get the update request.
        // The timestamp is required for the Time Machine.
        doEditFile({
          data: {
            ...found,
            // keep existing description if any
            // description: item.description?.trim() || found.description,
            description:
              item.description?.trim() === '' ? '' : item.description?.trim(),
          },
        });
      } else {
        callCreateFile();
      }
    }
  }, [
    shouldStartUpload,
    isFilesQueryEnabled,
    filesQuery.data,
    item.error,
    item.file.name,
    item.id,
    safeFileName,
  ]);

  const startNextQueuedUpload = () => {
    setFileProgressContext((prevState) => {
      const nextIndex = prevState.findIndex(
        (item) => !item.shouldStartUpload && !item.isDone && !item.error
      );

      if (nextIndex < 0) return prevState;

      const newList = [...prevState];

      newList[nextIndex] = {
        ...prevState[nextIndex],
        shouldStartUpload: true,
      };

      return newList;
    });
  };

  const markFileAsSkipped = () => {
    setPercent(100);

    // Resume mode: the file already has an uploaded version,
    // so we mark it as done without creating a new version.
    onFileDone({
      ...(item as FileProgress),
      isDone: true,
      shouldStartUpload: false,
    });

    // Keep bulk upload queue moving after skipped files.
    startNextQueuedUpload();

    setTimeout(() => {
      updateFileProgress(item.id, {
        isDone: true,
        entityCreated: true,
        shouldStartUpload: false,
      });
    }, 240);
  };

  /** creates a TusUpload instance for file, with context bound events, then starts the upload */
  const setupTusUpload = (dbFile: DbFile) => {
    const safeName = normalizeMsWordDashes(item.file.name);
    const commitId = item?.commit?.id;

    // file entity created in db, now do the upload
    const tusUpload = new TusUpload(item.file, {
      endpoint: `${apiCweb.defaults.baseURL}/files${
        commitId ? `?commit_id=${commitId}` : ''
      }`,
      chunkSize: CHUNK_SIZE,
      headers: {
        Authorization: apiCweb.defaults.headers.common.Authorization,
      },
      retryDelays: [1000, 3000, 6000],
      metadata: {
        project_id: String(item.projectId),
        // sanitize name for TUS metadata
        name: safeName,
        // links upload to db file
        versioned_file_id: String(dbFile.id),
        commit_id: String(item?.commit?.id),
        // add `item.versionStr` if set
        ...(item.versionStr
          ? {
              version: item.versionStr,
            }
          : {}),
        description: item.description || '',
      },
      // event handlers
      onError: (error) => {
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current += 1;
          tusUpload.start(); // Retry the upload
        } else {
          updateFileProgress(item.id, { error });
          retryCountRef.current = 0; // Reset retry count
          // Show retry option to the user
          setFileProgressContext((prevState) => {
            const nextIndex = prevState.findIndex(
              (item) => !item.shouldStartUpload && !item.isDone
            );
            if (nextIndex >= 0) {
              const newList = [...prevState];
              newList[nextIndex] = {
                ...prevState[nextIndex],
                shouldStartUpload: true,
              };
              return newList;
            } else return prevState;
          });
        }
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesUploaded) return;
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        throttledSetPercent(Number(percentage));
      },
      onSuccess: () => {
        setPercent(100);
        onFileDone({ ...item, isDone: true, shouldStartUpload: false });
        retryCountRef.current = 0; // Reset retry count on success
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        setTimeout(() => {
          updateFileProgress(item.id, { isDone: true });
        }, 240);
      },
    });

    // update context
    updateFileProgress(item.id, {
      error: undefined,
      entityCreated: true,
    });

    // set ref
    tusUploadRef.current = tusUpload;

    // start upload
    // for future, look into logic to do uploads 1by1 rather then starting them all
    tusUpload.start();
  };

  const setupTusUploadForCurrentCorrectionLetterAttachment = (
    fileItem: FileProgress,
    tenderId: string
  ) => {
    const safeName = normalizeMsWordDashes(fileItem.file.name);

    const endpoint = `${apiCweb.defaults.baseURL}/projects/${projectId}/tenders/${tenderId}/current_correction_letter/attachments/tus`;

    const tusUpload = new TusUpload(fileItem.file, {
      endpoint: endpoint,
      chunkSize: CHUNK_SIZE,
      headers: {
        Authorization: apiCweb.defaults.headers.common.Authorization,
      },
      metadata: {
        // sanitize name for TUS metadata
        name: safeName,
        version: fileItem.versionStr?.trim() ? fileItem.versionStr.trim() : ' ',
        description: fileItem.description?.trim()
          ? fileItem.description.trim()
          : ' ',
      },
      retryDelays: [1000, 3000, 6000],
      onError: (error) => {
        appLog(
          'CorrelationLetterAttachmentUpload -> tusUpload -> item',
          fileItem
        );
        appLog('CorrelationLetterAttachmentUpload -> tusUpload -> err', error);
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };

            return newList;
          } else return prevState;
        });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesUploaded) return;
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        throttledSetPercent(Number(percentage));
      },
      onSuccess: () => {
        setPercent(100);
        onAttachmentDone({ ...item, isDone: true, shouldStartUpload: false });

        // Refresh letter data so attachments render immediately
        queryCache.invalidateQueries([
          'current_correction_letter',
          Number(projectId),
          Number(tenderId),
        ]);

        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        setTimeout(() => {
          updateFileProgress(item.id, { isDone: true });
        }, 240);
      },
    });

    // Start the upload
    tusUpload.start();
  };

  const setupTusUploadForInstantMessageDraft = (
    fileItem: FileProgress,
    tenderId: string,
    draftId: number
  ) => {
    const safeName = normalizeMsWordDashes(fileItem.file.name);
    const endpoint = `${apiCweb.defaults.baseURL}/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts/${draftId}/attachments/tus`;

    const tusUpload = new TusUpload(fileItem.file, {
      endpoint: endpoint,
      chunkSize: CHUNK_SIZE,
      headers: {
        Authorization: apiCweb.defaults.headers.common.Authorization,
      },
      metadata: {
        name: safeName,
        version: fileItem.versionStr?.trim() ? fileItem.versionStr.trim() : ' ',
        description: fileItem.description?.trim()
          ? fileItem.description.trim()
          : ' ',
      },
      retryDelays: [1000, 3000, 6000],
      onError: (error) => {
        appLog('InstantMessageAttachmentUpload -> tusUpload -> err', error);
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesUploaded) return;
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        throttledSetPercent(Number(percentage));
      },
      onSuccess: () => {
        setPercent(100);
        onAttachmentDone({ ...item, isDone: true, shouldStartUpload: false });

        queryCache.invalidateQueries([
          'instant_message_drafts',
          Number(projectId),
          Number(tenderId),
        ]);

        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        setTimeout(() => {
          updateFileProgress(item.id, { isDone: true });
        }, 240);
      },
    });

    tusUpload.start();
  };

  const setupTusUploadForEvaluationInstantMessage = (
    fileItem: FileProgress,
    tenderId: string,
    contractId: string
  ) => {
    const safeName = normalizeMsWordDashes(fileItem.file.name);
    const endpoint = `${apiCweb.defaults.baseURL}/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/evaluation/current_instant_message/attachments/tus`;

    const tusUpload = new TusUpload(fileItem.file, {
      endpoint: endpoint,
      chunkSize: CHUNK_SIZE,
      headers: {
        Authorization: apiCweb.defaults.headers.common.Authorization,
      },
      metadata: {
        name: safeName,
        version: fileItem.versionStr?.trim() ? fileItem.versionStr.trim() : ' ',
        description: fileItem.description?.trim()
          ? fileItem.description.trim()
          : ' ',
      },
      retryDelays: [1000, 3000, 6000],
      onError: (error) => {
        appLog(
          'EvaluationInstantMessageAttachmentUpload -> tusUpload -> err',
          error
        );
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesUploaded) return;
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        throttledSetPercent(Number(percentage));
      },
      onSuccess: () => {
        setPercent(100);
        onAttachmentDone({ ...item, isDone: true, shouldStartUpload: false });

        queryCache.invalidateQueries([
          'eval_current_instant_message',
          Number(projectId),
          Number(tenderId),
          Number(contractId),
        ]);

        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        setTimeout(() => {
          updateFileProgress(item.id, { isDone: true });
        }, 240);
      },
    });

    tusUpload.start();
  };

  const setupTusUploadForTender = (
    fileItem: FileProgress,
    tenderId: string
  ) => {
    const safeName = normalizeMsWordDashes(fileItem.file.name);

    const endpoint = `${apiCweb.defaults.baseURL}/projects/${projectId}/tenders/${tenderId}/attachments/tus`;
    const tusUpload = new TusUpload(fileItem.file, {
      endpoint: endpoint,
      chunkSize: CHUNK_SIZE,
      headers: {
        Authorization: apiCweb.defaults.headers.common.Authorization,
      },
      metadata: {
        // sanitize name for TUS metadata
        name: safeName,
        version: fileItem.versionStr?.trim() ? fileItem.versionStr.trim() : ' ',
        description: fileItem.description?.trim()
          ? fileItem.description.trim()
          : ' ',
      },
      retryDelays: [1000, 3000, 6000],
      onError: (error) => {
        appLog('AttachmentProgressItem -> tusUpload -> item', fileItem);
        appLog('AttachmentProgressItem -> tusUpload -> err', error);
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };

            return newList;
          } else return prevState;
        });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesUploaded) return;
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        throttledSetPercent(Number(percentage));
      },
      onSuccess: () => {
        setPercent(100);
        onAttachmentDone({ ...item, isDone: true, shouldStartUpload: false });
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        setTimeout(() => {
          updateFileProgress(item.id, { isDone: true });
        }, 240);
      },
    });

    // Start the upload
    tusUpload.start();
  };

  const setupTusUploadForVoucher = (
    fileItem: FileProgress,
    ids: {
      projectId: string;
      tenderId: string;
      contractId: string;
      voucherId: string;
    }
  ) => {
    const safeName = normalizeMsWordDashes(fileItem.file.name);

    const { projectId, tenderId, contractId, voucherId } = ids;

    const endpoint = `${apiCweb.defaults.baseURL}/projects/${projectId}/tenders/${tenderId}/contracts/${contractId}/vouchers/${voucherId}/attachments/tus`;

    const tusUpload = new TusUpload(fileItem.file, {
      endpoint,
      chunkSize: CHUNK_SIZE,
      headers: {
        Authorization: apiCweb.defaults.headers.common.Authorization,
      },
      metadata: {
        // sanitize name for TUS metadata
        name: safeName,
        version: fileItem.versionStr?.trim() || ' ',
        description: fileItem.description?.trim() || ' ',
      },
      retryDelays: [1000, 3000, 6000],
      onError: (error) => {
        appLog('VoucherUpload -> tusUpload -> item', fileItem);
        appLog('VoucherUpload -> tusUpload -> err', error);
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesUploaded) return;
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        throttledSetPercent(Number(percentage));
      },
      onSuccess: () => {
        setPercent(100);
        onAttachmentDone({
          ...fileItem,
          isDone: true,
          shouldStartUpload: false,
        });

        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        setTimeout(() => {
          updateFileProgress(fileItem.id, { isDone: true });
        }, 240);
      },
    });

    // console.log(
    //   `TUS: fileProgressItem: upload started for voucher ${voucherId}`,
    //   {
    //     ids,
    //     fileItem,
    //   }
    // );
    tusUpload.start();
  };

  const setupTusUploadForVendorVoucherResponse = (
    fileItem: FileProgress,
    ids: {
      shareId: string;
      shareKey: string;
      offerId: number;
      voucherId: string;
    }
  ) => {
    const safeName = normalizeMsWordDashes(fileItem.file.name);

    const { shareId, shareKey, offerId, voucherId } = ids;

    const endpoint = `${apiCweb.defaults.baseURL}/tender_shares/${shareId}/offers/${offerId}/vouchers/${voucherId}/responses/attachment/tus`;

    const tusUpload = new TusUpload(fileItem.file, {
      endpoint,
      chunkSize: CHUNK_SIZE,
      headers: {
        Authorization: apiCweb.defaults.headers.common.Authorization,
        'Share-Key': shareKey,
      },
      metadata: {
        // sanitize name for TUS metadata
        name: safeName,
        version: fileItem.versionStr?.trim() || ' ',
        description: fileItem.description?.trim() || ' ',
      },
      retryDelays: [1000, 3000, 6000],
      onError: (error) => {
        appLog('VendorVoucherResponseUpload -> tusUpload -> item', fileItem);
        appLog('VendorVoucherResponseUpload -> tusUpload -> err', error);

        // error display
        updateFileProgress(fileItem.id, { error });
        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        message.error(
          intl.formatMessage({ id: 'attachment-upload-failed' }) ||
            'Attachment upload failed'
        );
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesUploaded) return;
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        throttledSetPercent(Number(percentage));
      },
      onSuccess: () => {
        setPercent(100);
        onAttachmentDone({
          ...fileItem,
          isDone: true,
          shouldStartUpload: false,
        });

        setFileProgressContext((prevState) => {
          const nextIndex = prevState.findIndex(
            (item) => !item.shouldStartUpload && !item.isDone
          );
          if (nextIndex >= 0) {
            const newList = [...prevState];
            newList[nextIndex] = {
              ...prevState[nextIndex],
              shouldStartUpload: true,
            };
            return newList;
          } else return prevState;
        });

        setTimeout(() => {
          updateFileProgress(fileItem.id, { isDone: true });
        }, 240);
      },
    });

    // console.log(`TUS: vendor voucher response upload started`, {
    //   ids,
    //   fileItem,
    // });

    tusUpload.start();
  };

  const throttledSetPercent = throttle(setPercent, 200);

  const callCreateFile = useCallback(() => {
    const safeName = normalizeMsWordDashes(item.file.name);

    const payload = {
      data: {
        // keep db title aligned with sanitized file name
        title: safeName,
        project_id: item.projectId,
        folder_id: folderId,
        description: item.description || '',
        state: DB_STATE.PARTIAL,
        tags: item.tags?.length ? item.tags : null,
      },
      commit_id: item?.commit?.id,
    };

    if (isPayloadSame(payload)) {
      return; // Skip sending the request if the payload is the same
    }

    lastPayloadRef.current = payload; // Update the last payload
    calledCreateRef.current = true;
    mutationCreateFile.reset();

    shouldStartUpload &&
      setTimeout(() => {
        doCreateFile(payload);
      }, 240);
  }, [doCreateFile, item, folderId, mutationCreateFile, shouldStartUpload]);

  /** convenient fc to update specific item of fileProgressContext
   *
   * @param id - id of the item to update
   * @param updates - properties to replace
   */
  const updateFileProgress = (id: string, updates: Partial<FileProgress>) => {
    setFileProgressContext((prevFileProgress) =>
      prevFileProgress.map((prevItem) => {
        if (prevItem.id !== id) return prevItem;
        return { ...prevItem, ...updates };
      })
    );
  };

  const handleRetryClick = () => {
    updateFileProgress(item.id, { error: undefined });

    if (mutationCreateFile.isError || mutationEditFile.isError) {
      calledCreateRef.current = false;
    } else if (filesQuery.isError) {
      // loadFiles failed
      filesQuery.refetch();
    } else {
      // upload failed
      tusUploadRef.current?.start();
    }
  };

  const handleDeleteClick = () => {
    onFileDone(item, { isFileDelete: true });

    setTimeout(() => {
      setFileProgressContext((prevItems) =>
        prevItems.filter((v) => v.id !== item.id)
      );
    }, 240);
  };

  const Content = (
    <Fragment>
      <Progress
        type="circle"
        width={36}
        strokeWidth={18}
        percent={percent}
        status={item.error ? 'exception' : item.isDone ? 'success' : 'normal'}
        css={{
          '.ant-progress-inner .ant-progress-text': [
            {
              fontSize: `${percent < 100 ? 9 : 7.5}px !important`,
            },
            isPending && styles.pendingText,
          ],
        }}
      />
      <div
        css={[
          styles.name,
          isPending && styles.pendingText,
          item.error && styles.errorText,
        ]}>
        {item.file.name}
      </div>
    </Fragment>
  );

  if (item.error) {
    /** if error -> clickable dropdown with options for delete & retry */
    return (
      <Tooltip
        title={intl.formatMessage({ id: 'retry-upload' })}
        placement="left">
        <Dropdown
          placement="topLeft"
          trigger={['click']}
          overlay={
            <Menu>
              <Menu.Item key="delete" danger onClick={handleDeleteClick}>
                {intl.formatMessage({ id: 'delete' })}
              </Menu.Item>
              <Menu.Item key="retry" onClick={handleRetryClick}>
                {intl.formatMessage({ id: 'retry-upload' })}
              </Menu.Item>
            </Menu>
          }>
          <div css={[styles.container, styles.containerClickable]}>
            {Content}
          </div>
        </Dropdown>
      </Tooltip>
    );
  }

  return <div css={styles.container}>{Content}</div>;
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    padding: '4px 6px 4px 4px',
    marginBottom: 8,
    marginRight: 2,
    alignItems: 'center',
    borderRadius: 4,
    border: '1px solid transparent',
    animation: `${fadeInKeyframes} 0.3s ease-out`,
  },
  containerClickable: {
    transition: 'all 0.2s ease-out',
    border: '1px solid red',
    cursor: 'pointer',
    ':hover': {
      boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.21)',
    },
  },
  name: {
    marginLeft: 12,
    transition: 'all 0.2s ease-out',
  },
  pendingText: {
    color: 'orange',
  },
  errorText: {
    color: theme.colors.red,
  },
});
